<script lang="ts">
	import { run, preventDefault } from 'svelte/legacy';

	import { inputClasses } from '$lib/InputText.svelte';
	import IconBroadcast from '~icons/ri/megaphone-line';
	import { sendBroadcast } from './page.telefunc';
	import { currentEvent } from '$lib/currentEvent';
	import LoadingButton from '$lib/LoadingButton.svelte';
	import AlertBanner from '$lib/AlertBanner.svelte';
	import { Attending, ContactPreference } from '$lib/eventTypes';
	import { paypalLink } from '$lib/common';
	import { fade } from 'svelte/transition';
	import Switch from '$lib/components/Switch.svelte';
	import Modal2 from '$lib/Modal2.svelte';
	import { disabledButtonClasses } from '$lib/actions/setLoadingState';
	import * as m from '$lib/paraglide/messages.js';

	interface Props {
		[key: string]: any;
	}

	let { ...rest }: Props = $props();

	let open = $state(false);
	let message = $state('');
	let error: any = $state();
	let pinMessage = $state(false);
	let step: 'input' | 'donate' | 'success' = $state('input');

	let guests = $derived(
		$currentEvent.guests.filter((x) => x.attending === Attending.ATTENDING || x.attending === Attending.MAYBE)
	);
	let guestsWithContact = $derived(
		guests.filter(
			(x) =>
				x.contactPreference !== ContactPreference.NONE &&
				(x.contactPreference === ContactPreference.EMAIL ? x.email : x.phone)
		)
	);
	let guestsWithEmail = $derived(guestsWithContact.filter((x) => x.contactPreference === ContactPreference.EMAIL));
	let guestsWithSms = $derived(guestsWithContact.filter((x) => x.contactPreference === ContactPreference.PHONE));

	async function send() {
		if (guestsWithContact.length === 0) return;

		try {
			if (confirm(m.messageBroadcast_confirmSend()) === false) {
				return;
			}
			const result = await sendBroadcast($currentEvent.id, message, pinMessage);
			if (result?.error) throw result.error;
			step = 'success';
		} catch (e) {
			console.error(e);
			error = e;
		}
	}

	function next() {
		if (guestsWithContact.length === 0) return;
		step = 'donate';
	}

	run(() => {
		if (!open) {
			message = '';
			error = undefined;
			step = 'input';
		}
	});

	let subject = $derived(`Info zu ${$currentEvent.name}`);

	const maxlength = 320;
</script>

<button class="btn btn-secondary {rest.class}" onclick={() => (open = !open)}>
	<IconBroadcast class="-mt-1 mr-1 inline-block size-6" />
	{m.messageBroadcast_button()}
</button>

<Modal2 bind:open closeButton={true}>
	<div class="">
		{#if step === 'input'}
			<form onsubmit={preventDefault(next)}>
				<p class="label">{m.messageBroadcast_title()}</p>
				<p class="mt-1 text-sm text-gray-500">
					{m.messageBroadcast_description()}
					{m.messageBroadcast_guestsWithContact({
						guestsWithContact: guestsWithContact.length,
					})}
				</p>

				<div class="mt-4 flex w-fit flex-wrap">
					<label for="companionsEnabled" class="flex flex-grow flex-col">
						<div class="flex items-center justify-between align-middle">
							<span class="text-sm text-gray-600">{m.messageBroadcast_pinMessage()}</span>
							<Switch bind:checked={pinMessage} class="ml-8" name="companionsEnabled" />
						</div>
					</label>
				</div>

				<div class="my-4 sm:my-6">
					<div
						class={`${inputClasses} m-[1px] w-full !p-0 focus-within:m-0 focus-within:border-2 focus-within:border-violet-500`}
					>
						<div class={`w-full border-b border-violet-300 px-4 py-3`}>
							{m.messageBroadcast_infoAbout({ eventName: $currentEvent.name })}
						</div>
						<textarea
							bind:value={message}
							class={`${inputClasses} ! w-full !border-none !shadow-none !ring-0 `}
							id="message"
							maxlength={maxlength - subject.length}
							rows="3"
							required
						></textarea>
					</div>
					<div
						class="ml-2 mt-1 text-sm text-gray-500"
						class:text-red-600={maxlength - (subject.length + message.length) === 0}
					>
						{m.messageBroadcast_characters({ current: message.length + subject.length, max: maxlength })}
					</div>
				</div>

				<div class="flex justify-end">
					<button
						class="btn btn-primary {guestsWithContact.length === 0 ? disabledButtonClasses.join(' ') : ''}"
						type="submit"
						disabled={guestsWithContact.length === 0}
					>
						{#if guestsWithContact.length > 0}
							{m.messageBroadcast_next()}
						{:else}
							{m.messageBroadcast_noContacts()}
						{/if}
					</button>
				</div>
			</form>
		{:else if step === 'donate'}
			<div class="mt-2" in:fade>
				<div class="text-centesr">
					<p>{@html m.messageBroadcast_donateText()}</p>
					<p>{m.messageBroadcast_donateMore()}</p>
				</div>

				<div class="my-8 flex justify-center">
					<a href={paypalLink('BachSamuel', 1)} target="_blank" class="btn btn-secondary">
						{m.messageBroadcast_donate()}
					</a>
				</div>

				{#if error}
					<AlertBanner type="error" class="my-4">
						<b class="font-bold">{m.messageBroadcast_errorTitle()}</b>
						<p>{error}</p>
					</AlertBanner>
				{/if}

				<div class="my-4 flex justify-center">
					<div></div>
					<LoadingButton class="btn btn-primary" onClick={send}>
						{m.messageBroadcast_sendTo({ count: guestsWithContact.length })}
						<IconBroadcast class="mb-1 ml-1 inline-block size-5" />
					</LoadingButton>
				</div>
			</div>
		{:else if step === 'success'}
			<div class="mt-6" in:fade>
				<AlertBanner type="success">
					{m.messageBroadcast_successMessage({ count: guestsWithContact.length })}
				</AlertBanner>

				<div class="mb-4 mt-8 flex justify-center">
					<button class="btn btn-primary w-full" onclick={() => (open = false)}>
						{m.messageBroadcast_okay()}
					</button>
				</div>
			</div>
		{/if}
	</div>
</Modal2>
